import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { useAuth } from '../Contexts/AuthContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { AssistantApi, AssistantCreateApi } from '../Services/AgentApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

function AgentsHub({ canEdit }: { canEdit: boolean }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { status } = useAuth();
  const isDocumentPermission = status?.permissions?.includes('all:document');
  const {
    getAgents,
    deleteAgent,
    getAgentNotifications,
    getAgentCatalog,
    getAgentFrames,
    getAgentMarkers,
    createAgent,
    getAgentSwarm,
  } = useAgentApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [agents, setAgents] = useState<AssistantApi[]>([]);
  const [totalAgents, setTotalAgents] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [selectedRows, setSelectedRows] = useState<AssistantApi[]>([]);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].key : null;

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const assistantTypes = {
    SIMPLE: 'Simple',
    RAG: 'RAG',
  };

  const columns = [
    { name: 'ID', selector: (row: AssistantApi) => row.key, sortable: false },
    { name: 'Name', selector: (row: AssistantApi) => row.name, sortable: false },
    {
      name: 'Type',
      selector: (row: AssistantApi) => assistantTypes[row.assistant_type],
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: AssistantApi) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: AssistantApi) => formatDateUTC(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: AssistantApi) => formatDateUTC(row.updated_at),
      sortable: false,
    },
  ];

  const handleRowClicked = (row: AssistantApi): void => {
    navigate(`/organizations/${organizationId}/agents-hub/${row.key}`, {
      state: { organizationId },
    });
  };

  const handleDelete = async (selectedId: string[]): Promise<void> => {
    if (!selectedId[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteAgent(selectedId[0]);
        await fetchAgents(0, itemsPerPage);
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const createCopy = async (): Promise<void> => {
    if (!selectedId) return;
    setLoading(true);

    try {
      const agentToCopy = agents.find((agent) => agent.key === selectedId);
      if (!agentToCopy) {
        showAlert('fail', 'Agent not found');
        setLoading(false);
        return;
      }

      const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const baseName = agentToCopy.name;
      const safeBaseName = escapeRegExp(baseName);
      const copyNumbers = agents
        .map((a) => {
          const match = a.name.match(new RegExp(`^${safeBaseName} \\(Copy (\\d+)\\)$`));
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null);
      const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

      const [notificationsData, framesData, markersData, swarmsData, catalogData] =
        await Promise.all([
          getAgentNotifications(selectedId).then((n) => n?.map((a) => a.id) ?? []),
          getAgentFrames(selectedId).then((f) => f?.map((a) => a.id) ?? []),
          getAgentMarkers(selectedId).then((m) => m?.map((a) => a.id) ?? []),
          getAgentSwarm(selectedId).then((s) => s?.map((s) => s.key) ?? []),
          isDocumentPermission
            ? getAgentCatalog(selectedId).then((c) => c?.id ?? null)
            : Promise.resolve(null as string | null),
        ]);

      const newAgentData: AssistantCreateApi = {
        name: `${baseName} (Copy ${nextCopyNumber})`,
        prompt_id: agentToCopy.prompt_id,
        assistant_type: agentToCopy.assistant_type,
        temperature: agentToCopy.temperature,
        details_level: agentToCopy.details_level,
        model_name: agentToCopy.model_name,
        reasoning: agentToCopy.reasoning ?? 'minimal',
        memory_type: agentToCopy.memory_type,
        memory_limit: agentToCopy.memory_limit,
        document_collection_keys: agentToCopy.document_collection_keys,
        information_package_collection_keys: agentToCopy.information_package_collection_keys,
        connections: agentToCopy.connections,
        taxonomy_dictionary_id: agentToCopy.taxonomy_dictionary_id,
        skills: agentToCopy.skills,
        templates: agentToCopy.templates,
        is_active: agentToCopy.is_active,
        frame_keys: framesData,
        marker_keys: markersData,
        swarm: swarmsData,
        notification_keys: notificationsData,
        catalog_key: catalogData,
      };

      await createAgent(newAgentData);

      await fetchAgents(0, itemsPerPage);

      showAlert('success', 'Agent copy was successfully created!');
    } catch (error) {
      console.error('Create copy error:', error);
      showAlert('fail', 'Failed to create a copy of the agent!');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getAgents(skip, limit);
      setAgents(response.items);
      setTotalAgents(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch agents!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Agents"
                columns={columns}
                data={agents}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={itemsPerPage}
                progressPending={loading}
                paginationTotalRows={totalAgents}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchAgents}
                getRowUrl={(row) => `/organizations/${organizationId}/agents-hub/${row.key}`}
                onSelectedRowsChange={({ selectedRows }) => {
                  setSelectedRows(selectedRows);
                }}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/agents-hub/new-agent`)}
                >
                  Create New Agent
                </Button>
              )}
              {selectedId && (
                <Button variant="ghost" size="medium" onClick={createCopy}>
                  Create copy
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentsHubWrapper({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <AgentsHub canEdit={canEdit} />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentsHubWrapper;
